import { PureComponent } from 'react';
import { MapContext, layer, source, feature } from 'react-openlayers';
import { Coordinate } from 'ol/coordinate';
import { MoveActionInfo } from '../../../services/LassoService';
import { convertProjection4329 } from '../../../utils';

interface LassoMoveClickLayerProps {
  /**
   * Whether we are currently waiting for the user to click the map to complete a Move action
   */
  waitingForClick: boolean;
  /**
   * Callback when the map is clicked during move action
   */
  onMapClick: (coordinate: Coordinate) => void;
  /**
   * Information about the move action - includes samples and original center
   */
  moveInfo?: MoveActionInfo;
}

interface LassoMoveClickLayerState {
  /**
   * Current mouse position in map coordinates
   */
  mousePosition: Coordinate | null;
}

/**
 * Layer component that handles map clicks for completing lasso move actions.
 * This component listens for map clicks when waiting for a new center position
 * during the Move action.
 */
export default class LassoMoveClickLayer extends PureComponent<LassoMoveClickLayerProps, LassoMoveClickLayerState> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  // See MapInteractionsLayer.waitForMap for the full rationale. Any
  // unmount with a pending retry leaks the callback onto the old
  // instance, which then attaches a SECOND singleclick/pointermove
  // listener to the live map when `waitingForClick` is true —
  // handlers then fire twice per event.
  waitForMapTimer: NodeJS.Timeout | undefined;

  constructor(props: LassoMoveClickLayerProps) {
    super(props);
    this.state = {
      mousePosition: null,
    };
  }

  componentDidMount() {
    this.waitForMap();
  }

  componentWillUnmount() {
    clearTimeout(this.waitForMapTimer);
    this.waitForMapTimer = undefined;
    this.removeMapClickListener();
    this.removeMouseMoveListener();
  }

  componentDidUpdate(prevProps: LassoMoveClickLayerProps) {
    // If waitingForClick changed to true, ensure listener is attached
    if (!prevProps.waitingForClick && this.props.waitingForClick) {
      this.attachMapClickListener();
      this.attachMouseMoveListener();
    }

    // If waitingForClick changed to false, remove listener
    if (prevProps.waitingForClick && !this.props.waitingForClick) {
      this.removeMapClickListener();
      this.removeMouseMoveListener();
      this.setState({ mousePosition: null });
    }
  }

  waitForMap = () => {
    if (this.context?.map) {
      // Map is ready
      if (this.props.waitingForClick) {
        this.attachMapClickListener();
        this.attachMouseMoveListener();
      }
    } else {
      this.waitForMapTimer = setTimeout(this.waitForMap, 100); // try again in 100ms
    }
  };

  attachMapClickListener = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    // Remove existing listener first (if any)
    this.removeMapClickListener();

    // Add the click listener
    map.on('singleclick', this.handleMapClick);
  };

  removeMapClickListener = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    // Remove the click listener
    map.un('singleclick', this.handleMapClick);
  };

  attachMouseMoveListener = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    // Remove existing listener first (if any)
    this.removeMouseMoveListener();

    // Add the pointer move listener
    map.on('pointermove', this.handleMouseMove);
  };

  removeMouseMoveListener = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    // Remove the pointer move listener
    map.un('pointermove', this.handleMouseMove);
  };

  handleMouseMove = (event: any) => {
    // Only track mouse position if we're waiting for a click
    if (!this.props.waitingForClick) {
      return;
    }

    // Get the coordinate from the pointer move event
    const coordinate = event.coordinate as Coordinate;

    // Update state with new mouse position
    this.setState({ mousePosition: coordinate });
  };

  handleMapClick = (event: any) => {
    // Only handle the click if we're waiting for it
    if (!this.props.waitingForClick) {
      return;
    }

    // Get the coordinate from the click event
    const coordinate = event.coordinate as Coordinate;

    // Call the callback with the clicked coordinate
    this.props.onMapClick(coordinate);
  };

  /**
   * Calculate bounding box from samples
   */
  calculateBoundingBox(): Coordinate[][] | null {
    if (!this.props.moveInfo) {
      return null;
    }

    const { samplesWithCentroids } = this.props.moveInfo;

    if (samplesWithCentroids.length === 0) {
      return null;
    }

    // Find min/max lat/lon
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;

    for (const { centroid } of samplesWithCentroids) {
      minLat = Math.min(minLat, centroid.lat);
      maxLat = Math.max(maxLat, centroid.lat);
      minLon = Math.min(minLon, centroid.lon);
      maxLon = Math.max(maxLon, centroid.lon);
    }

    // Create polygon coordinates (rectangle)
    // Note: coordinates are in [lon, lat] format for OpenLayers
    return [
      [
        convertProjection4329([minLon, minLat]), // bottom-left
        convertProjection4329([maxLon, minLat]), // bottom-right
        convertProjection4329([maxLon, maxLat]), // top-right
        convertProjection4329([minLon, maxLat]), // top-left
        convertProjection4329([minLon, minLat]), // close the polygon
      ],
    ];
  }

  /**
   * Calculate the offset between original center and new position (mouse cursor)
   */
  calculateOffset(newCenter: Coordinate): [number, number] {
    if (!this.props.moveInfo) {
      return [0, 0];
    }

    const { originalCenter } = this.props.moveInfo;
    const originalCenterProjected = convertProjection4329(originalCenter.slice().reverse());

    // Both originalCenter and newCenter are in EPSG:3857 (map projection)
    const offsetX = newCenter[0] - originalCenterProjected[0];
    const offsetY = newCenter[1] - originalCenterProjected[1];

    return [offsetX, offsetY];
  }

  /**
   * Calculate preview bounding box at the new position (mouse cursor)
   */
  calculatePreviewBoundingBox(newCenter: Coordinate): Coordinate[][] | null {
    if (!this.props.moveInfo) {
      return null;
    }

    const { samplesWithCentroids } = this.props.moveInfo;

    if (samplesWithCentroids.length === 0) {
      return null;
    }

    const [offsetX, offsetY] = this.calculateOffset(newCenter);

    // Find min/max lat/lon
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;

    for (const { centroid } of samplesWithCentroids) {
      minLat = Math.min(minLat, centroid.lat);
      maxLat = Math.max(maxLat, centroid.lat);
      minLon = Math.min(minLon, centroid.lon);
      maxLon = Math.max(maxLon, centroid.lon);
    }

    // Convert to map projection and apply offset
    const bottomLeft = convertProjection4329([minLon, minLat]);
    const bottomRight = convertProjection4329([maxLon, minLat]);
    const topRight = convertProjection4329([maxLon, maxLat]);
    const topLeft = convertProjection4329([minLon, maxLat]);

    // Create polygon coordinates (rectangle) with offset applied
    return [
      [
        [bottomLeft[0] + offsetX, bottomLeft[1] + offsetY],
        [bottomRight[0] + offsetX, bottomRight[1] + offsetY],
        [topRight[0] + offsetX, topRight[1] + offsetY],
        [topLeft[0] + offsetX, topLeft[1] + offsetY],
        [bottomLeft[0] + offsetX, bottomLeft[1] + offsetY], // close
      ],
    ];
  }

  /**
   * Calculate preview sample positions at the new position (mouse cursor)
   */
  calculatePreviewSamplePositions(newCenter: Coordinate): Coordinate[] {
    if (!this.props.moveInfo) {
      return [];
    }

    const { samplesWithCentroids } = this.props.moveInfo;
    const [offsetX, offsetY] = this.calculateOffset(newCenter);

    return samplesWithCentroids.map(({ centroid }) => {
      const samplePos = convertProjection4329([centroid.lon, centroid.lat]);

      return [samplePos[0] + offsetX, samplePos[1] + offsetY];
    });
  }

  render() {
    // If not waiting for click, don't render anything
    if (!this.props.waitingForClick || !this.props.moveInfo) {
      return null;
    }

    const boundingBox = this.calculateBoundingBox();
    const { originalCenter } = this.props.moveInfo;
    const centerProjected = convertProjection4329(originalCenter.slice().reverse());

    // Calculate preview elements if mouse is hovering
    const { mousePosition } = this.state;
    const previewBoundingBox = mousePosition ? this.calculatePreviewBoundingBox(mousePosition) : null;
    const previewSamplePositions = mousePosition ? this.calculatePreviewSamplePositions(mousePosition) : [];

    return (
      // @ts-ignore
      <layer.Vector zIndex={9999}>
        {/* @ts-ignore */}
        <source.VectorSourceReact>
          {/* ORIGINAL POSITION - Orange */}
          {/* Render original bounding box */}
          {boundingBox && (
            <feature.PolygonReact
              key="lasso-move-bounding-box-original"
              coordinates={boundingBox}
              fillOptions={{
                color: 'rgba(255, 165, 0, 0.2)', // Orange with transparency
              }}
              strokeOptions={{
                color: 'rgba(255, 140, 0, 0.8)', // Darker orange
                width: 3,
                lineDash: [10, 5], // Dashed line
              }}
            />
          )}

          {/* Render original center point */}
          <feature.PointReact
            key="lasso-move-center-original"
            coordinate={centerProjected}
            circleOptions={{
              radius: 10,
              fillOptions: {
                color: 'rgba(255, 140, 0, 0.8)', // Orange
              },
              strokeOptions: {
                color: 'rgba(255, 255, 255, 1)', // White outline
                width: 3,
              },
            }}
          />

          {/* Original center crosshair */}
          <feature.PointReact
            key="lasso-move-center-crosshair-original"
            coordinate={centerProjected}
            circleOptions={{
              radius: 3,
              fillOptions: {
                color: 'rgba(255, 255, 255, 1)', // White center dot
              },
              strokeOptions: {
                color: 'rgba(0, 0, 0, 0.5)',
                width: 1,
              },
            }}
          />

          {/* PREVIEW POSITION - Cyan/Blue (follows mouse cursor) */}
          {mousePosition && previewBoundingBox && (
            <>
              {/* Render preview bounding box */}
              <feature.PolygonReact
                key="lasso-move-bounding-box-preview"
                coordinates={previewBoundingBox}
                fillOptions={{
                  color: 'rgba(0, 191, 255, 0.2)', // Cyan with transparency
                }}
                strokeOptions={{
                  color: 'rgba(0, 150, 255, 0.9)', // Darker cyan
                  width: 3,
                  lineDash: [5, 5], // Different dash pattern
                }}
              />

              {/* Render preview center point */}
              <feature.PointReact
                key="lasso-move-center-preview"
                coordinate={mousePosition}
                circleOptions={{
                  radius: 10,
                  fillOptions: {
                    color: 'rgba(0, 150, 255, 0.9)', // Cyan
                  },
                  strokeOptions: {
                    color: 'rgba(255, 255, 255, 1)', // White outline
                    width: 3,
                  },
                }}
              />

              {/* Preview center crosshair */}
              <feature.PointReact
                key="lasso-move-center-crosshair-preview"
                coordinate={mousePosition}
                circleOptions={{
                  radius: 3,
                  fillOptions: {
                    color: 'rgba(255, 255, 255, 1)', // White center dot
                  },
                  strokeOptions: {
                    color: 'rgba(0, 0, 0, 0.5)',
                    width: 1,
                  },
                }}
              />

              {/* Render preview sample positions as small dots */}
              {previewSamplePositions.map((position, index) => (
                <feature.PointReact
                  key={`lasso-move-sample-preview-${index}`}
                  coordinate={position}
                  circleOptions={{
                    radius: 4,
                    fillOptions: {
                      color: 'rgba(0, 191, 255, 0.6)', // Semi-transparent cyan
                    },
                    strokeOptions: {
                      color: 'rgba(255, 255, 255, 0.8)',
                      width: 2,
                    },
                  }}
                />
              ))}
            </>
          )}
        </source.VectorSourceReact>
      </layer.Vector>
    );
  }
}
